<svelte:options runes={true} />

<script lang="ts">
  import ProductMiniCard from "~/components/product/ProductMiniCard/ProductMiniCard.svelte";
  import NavMenuItemHeader from "~/components/ui/NavMenuItemHeader/NavMenuItemHeader.svelte";
  import Button from "~/components/ui/Button/Button.svelte";
  import IconArrowRight from "~icons/dusk/arrow-right";
  import { AUTOSUGGEST_PRODUCT_ROWS } from "~/lib/listings/create-autosuggest.svelte";
  import type { AutosuggestItem } from "~/lib/search/create-search-autosuggest-core.svelte";

  interface Props {
    items?: Extract<AutosuggestItem, { kind: "product" }>[];
    showAll?: Extract<AutosuggestItem, { kind: "showAll" }>;
    highlightedItemId?: string | undefined;
    vertical: boolean;
    /**
     * Loading state. Renders the same wrapper, gaps and card component as the real
     * results, so the panel doesn't reflow when they land.
     */
    skeleton?: boolean;
    /**
     * How many placeholders the skeleton draws. The shells pass what they have measured
     * room for, so the skeleton occupies exactly the space the results will.
     */
    skeletonCount?: number;
    class?: string;
  }

  let {
    items = [],
    showAll,
    highlightedItemId,
    vertical,
    skeleton = false,
    skeletonCount = AUTOSUGGEST_PRODUCT_ROWS,
    class: className = "",
  }: Props = $props();

  // Never settles, so ProductMiniCard stays in its own {#await} skeleton branch —
  // the only way to get a placeholder that is exactly card-shaped.
  const PENDING = new Promise<never>(() => {});

  const skeletonCards = $derived(Array.from({ length: skeletonCount }, (_, i) => i));
</script>

<div
  class={["da-product-suggestion-list", className]}
  role={skeleton ? undefined : "group"}
  aria-label={skeleton ? undefined : "Products"}
  aria-hidden={skeleton ? "true" : undefined}
>
  <div
    class={[
      "da-product-suggestion-list__cards",
      vertical && "da-product-suggestion-list__cards--vertical",
    ]}
  >
    <!-- Exclusive: the two queries settle independently, so the products one landing
         first would otherwise draw real cards and placeholders side by side in a nowrap
         row, inside the aria-hidden wrapper above. -->
    {#if skeleton}
      {#each skeletonCards as i (i)}
        <ProductMiniCard data={PENDING} {vertical} />
      {/each}
    {:else}
      {#each items as item (item.id)}
        <ProductMiniCard
          data={item.product}
          {vertical}
          optionId={item.id}
          selected={item.id === highlightedItemId}
        />
      {/each}
    {/if}
  </div>
  {#if skeleton}
    <div
      class={[
        "da-skeleton",
        "da-product-suggestion-list__show-all-skeleton",
        vertical && "da-product-suggestion-list__show-all-skeleton--vertical",
      ]}
    ></div>
  {:else if showAll}
    {#if vertical}
      <Button
        variant="tertiary"
        color="charcoal"
        href={showAll.href}
        role="option"
        id={showAll.id}
        aria-selected={showAll.id === highlightedItemId}
        tabindex={-1}
      >
        Show all results
        {#snippet trailingIcon()}
          <IconArrowRight aria-hidden="true" width="20" height="20" />
        {/snippet}
      </Button>
    {:else}
      <NavMenuItemHeader
        label="Show all results"
        href={showAll.href}
        optionId={showAll.id}
        selected={showAll.id === highlightedItemId}
      />
    {/if}
  {/if}
</div>

<style>
  @layer aurora {
    .da-product-suggestion-list {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: var(--da-space-4);
      width: 100%;
    }

    .da-product-suggestion-list__cards {
      display: flex;
      flex-direction: column;
      gap: var(--da-space-3);
      width: 100%;
    }

    /* One row, never two: the panel is a preview, and a second row of cards pushes
       "Show all results" and the popular searches off the design. The shell caps the
       item count to what fits, and the clip covers the single frame between mount and
       that first measurement. `clip` rather than `hidden` so the cards can still paint
       outside the box: 10px covers --da-shadow-md's reach (4px offset + 8px blur - 2px
       spread) and the highlight's outline (2px + 2px offset) with room to spare. */
    .da-product-suggestion-list__cards--vertical {
      flex-direction: row;
      flex-wrap: nowrap;
      overflow: clip;
      overflow-clip-margin: 10px;
    }

    /* Sized to the real "Show all results" control it stands in for: the tertiary
       Button on desktop, the full-width NavMenuItemHeader row on mobile. */
    .da-product-suggestion-list__show-all-skeleton {
      width: 100%;
      height: 53px;
      border-radius: var(--da-radius-sm);
    }

    .da-product-suggestion-list__show-all-skeleton--vertical {
      width: 207px;
      height: 40px;
      border-radius: var(--da-radius-xl);
    }
  }
</style>
